import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Switch, Route } from 'react-router-dom';
import Cookies from 'js-cookie';
import moment from 'moment';
import get from 'lodash-es/get';
import {
  Button,
  PageHeader,
  Result,
  Skeleton,
  Descriptions,
  message,
  Input
} from 'antd';
import { Tag } from '@components/common';
import history from '@utils/history';
import ClientActions, { ClientSelectors } from '@redux/ClientRedux';
import { AppConfig } from '@utils/config';
import API from '@utils/api';
import { getErrors } from '@utils/error';
import { COLORS } from '@theme/styles';

import './ViewClient.less';

function ViewClientPage(props) {
  const {
    loadig,
    loadClient,
    resetClient,
    updateClient,
    clientData,
    match: { params, url }
  } = props;

  const { id: clientId } = params || {};
  const { email, internalNotes, isVIP, complaint } = clientData || {};
  const [adminNotes, setAdminNotes] = useState(internalNotes || '');
  const [isEditingInternal, setIsEditingInternal] = useState(false);
  const [isSubmittingInternal, setIsSubmittingInternal] = useState(false);

  const [nextCleanNotes, setNextCleanNotes] = useState(complaint || '');
  const [isEditingNextClean, setIsEditingNextClean] = useState(false);
  const [isSubmittingNextClean, setIsSubmittingNextClean] = useState(false);

  if (!clientId) {
    return (
      <div className="properties main-page">
        <PageHeader
          title="Client"
          style={{
            border: '1px solid rgb(235, 237, 240)'
          }}
          onBack={() => history.goBack()}
        />

        <div className="client main-content">
          <Result
            status="403"
            title="403"
            subTitle="Sorry, you are not authorized to access this page."
          />
        </div>
      </div>
    );
  }

  const handleToggleEditInternal = async () => {
    setIsEditingInternal(!isEditingInternal);
  };

  const handleToggleEditNextClean = async () => {
    setIsEditingNextClean(!isEditingNextClean);
  };

  const handleSaveInternalNotes = async () => {
    setIsSubmittingInternal(true);
    try {
      await API.saveInternalNote(clientId, email, adminNotes);
      updateClient({
        ...clientData,
        internalNotes: adminNotes
      });
    } catch (err) {
      const error = getErrors(err);
      message.error(error.message);
    }
    setIsSubmittingInternal(false);
    setIsEditingInternal(false);
  };

  const handleSaveNextCleanNotes = async () => {
    setIsSubmittingNextClean(true);
    try {
      await API.saveNextCleanNote(clientId, nextCleanNotes);
      updateClient({
        ...clientData,
        complaint: nextCleanNotes
      });
    } catch (err) {
      const error = getErrors(err);
      message.error(error.message);
      setNextCleanNotes(complaint);
    }
    setIsSubmittingNextClean(false);
    setIsEditingNextClean(false);
  };

  const handleLoginAsClient = async () => {
    const { userID } = clientData;
    const { result } = await API.getImpersonate(userID);
    const { authToken, refreshToken } = result;
    Cookies.set('authToken', authToken, { domain: AppConfig.DOMAIN });
    Cookies.set('refreshToken', refreshToken, { domain: AppConfig.DOMAIN });

    const win = window.open(AppConfig.WEB_APP_URL, '_blank');
    win.focus();
  };

  const toggleVIP = async () => {
    setIsSubmittingInternal(true);
    try {
      await API.setClientVIP(clientId, email, !isVIP);
      updateClient({
        ...clientData,
        isVIP: !isVIP
      });
    } catch (err) {
      const error = getErrors(err);
      message.error(error.message);
    }
    setIsSubmittingInternal(false);
  };

  const handleDownloadPdf = async () => {
    const { userID } = clientData;
    try {
      const {
        result: { keyReleasePDF }
      } = await API.fetchClientKeyRelease(userID);
      window.open(keyReleasePDF, '_blank');
    } catch (err) {
      const error = getErrors(err);
      message.error(error.message);
    }
  };

  const renderClient = () => {
    const {
      firstName,
      lastName,
      phone,
      buildingName,
      bedroomsCount,
      hasPets,
      unitNumber,
      propertyName,
      regionName,
      lastLoggedAt,
      createdAt,
      isKeyReleaseSigned
    } = clientData;

    const hasPackage =
      get(clientData, 'recurringBooking.status', 'no') === 'active';

    return (
      <div className="properties main-page">
        <PageHeader
          title={`${firstName} ${lastName}`}
          style={{
            border: '1px solid rgb(235, 237, 240)'
          }}
          onBack={() => history.goBack()}
        >
          <div
            className="content"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center'
            }}
          >
            <div
              style={{
                width: '80%'
              }}
            >
              <Descriptions size="small" column={3}>
                <Descriptions.Item label="Email">{email}</Descriptions.Item>
                <Descriptions.Item label="Created">
                  {moment(createdAt).format('LLL')}
                </Descriptions.Item>
                <Descriptions.Item label="VIP">
                  {isVIP && <Tag content="VIP" color={COLORS.cyan} />}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">{phone}</Descriptions.Item>
                <Descriptions.Item label="Last login">
                  {moment(lastLoggedAt).format('LLL')}
                </Descriptions.Item>
              </Descriptions>
            </div>
            <div className="extra">
              <Button
                type="primary"
                onClick={() => {
                  handleLoginAsClient();
                }}
              >
                Login as Client
              </Button>

              <br />

              <Button
                type="ghost"
                disabled={isSubmittingInternal}
                style={{ color: isVIP ? 'black' : 'green', marginTop: 10 }}
                onClick={() => {
                  toggleVIP();
                }}
              >
                {!isVIP ? 'Set as VIP' : 'Deactivate VIP'}
              </Button>
            </div>
          </div>
        </PageHeader>

        <div className="main-content client">
          <Descriptions bordered column={3} style={{ width: '100%' }}>
            <Descriptions.Item label="Property">
              {propertyName}
            </Descriptions.Item>
            <Descriptions.Item label="Building">
              {buildingName}
            </Descriptions.Item>
            <Descriptions.Item label="Unit Number">
              {unitNumber}
            </Descriptions.Item>
            <Descriptions.Item label="No of Bedrooms">
              {bedroomsCount}
            </Descriptions.Item>
            <Descriptions.Item label="Has Pets">
              {hasPets ? 'Yes' : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Region">{regionName}</Descriptions.Item>
            <Descriptions.Item label="Has Package" span={3}>
              {hasPackage && <Tag content="Yes" color={COLORS.cyan} />}
            </Descriptions.Item>
            <Descriptions.Item label="Key Release" span={3}>
              {isKeyReleaseSigned ? '' : 'Not Signed'}

              {isKeyReleaseSigned && (
                <Button type="primary" onClick={handleDownloadPdf}>
                  Download now!
                </Button>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Internal Notes" span={3}>
              {!isEditingInternal ? (
                internalNotes
              ) : (
                <Input.TextArea
                  value={adminNotes}
                  style={{ minHeight: 160 }}
                  onChange={e => {
                    setAdminNotes(e.target.value);
                  }}
                  placeholder="internal notes"
                />
              )}
              <div style={{ marginTop: 20 }}>
                {isEditingInternal && (
                  <Button
                    style={{ marginRight: 20 }}
                    type="primary"
                    disabled={isSubmittingInternal}
                    onClick={handleSaveInternalNotes}
                  >
                    Save
                  </Button>
                )}
                <Button
                  type="primary"
                  disabled={isSubmittingInternal}
                  onClick={handleToggleEditInternal}
                >
                  {!isEditingInternal ? 'Edit' : 'Cancel'}
                </Button>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Next Clean Notes" span={3}>
              {!isEditingNextClean ? (
                nextCleanNotes
              ) : (
                <Input.TextArea
                  value={nextCleanNotes}
                  style={{ minHeight: 160 }}
                  onChange={e => {
                    setNextCleanNotes(e.target.value);
                  }}
                  placeholder="next clean notes for housekeeper"
                />
              )}
              <div style={{ marginTop: 20 }}>
                {isEditingNextClean && (
                  <Button
                    style={{ marginRight: 20 }}
                    type="primary"
                    disabled={isSubmittingNextClean}
                    onClick={handleSaveNextCleanNotes}
                  >
                    Save
                  </Button>
                )}
                <Button
                  type="primary"
                  disabled={isSubmittingNextClean}
                  onClick={handleToggleEditNextClean}
                >
                  {!isEditingNextClean ? 'Edit' : 'Cancel'}
                </Button>
              </div>
            </Descriptions.Item>
          </Descriptions>
        </div>
      </div>
    );
  };

  useEffect(() => {
    loadClient(clientId);

    return () => {
      resetClient();
    };
  }, [clientId]);

  useEffect(() => {
    setAdminNotes(internalNotes);
  }, [internalNotes]);

  useEffect(() => {
    setNextCleanNotes(complaint);
  }, [complaint]);

  if (loadig) {
    return (
      <div className="properties main-page">
        <PageHeader
          style={{
            border: '1px solid rgb(235, 237, 240)'
          }}
          onBack={() => history.goBack()}
        />

        <div className="client main-content">
          <Skeleton />
        </div>
      </div>
    );
  }

  return (
    <Switch>
      <Route exact path={`${url}/`}>
        {renderClient()}
      </Route>
    </Switch>
  );
}

ViewClientPage.propTypes = {
  match: PropTypes.object,
  loadClient: PropTypes.func,
  resetClient: PropTypes.func,
  updateClient: PropTypes.func,
  loadig: PropTypes.bool,
  clientData: PropTypes.object
};

const mapStateToProps = state => ({
  loadig: ClientSelectors.selectIsLoading(state),
  clientData: ClientSelectors.selectClientData(state)
});

const mapDispatchToProps = dispatch => ({
  loadClient: clientId => {
    dispatch(ClientActions.loadClientRequest(clientId));
  },
  updateClient: clientData => {
    dispatch(ClientActions.setClientData(clientData));
  },
  resetClient: () => dispatch(ClientActions.resetClient())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ViewClientPage);
